import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Path from '../../components/path/path';
import paynectaService from '../../services/paynectaService';
import { supabase } from '../../supabaseClient';
import './Checkout.css';

const Checkout = ({ Data, SetData }) => {
    const cartItems = Data.Cart;
    const subtotal = cartItems.reduce(
        (acc, item) => acc + item.productQuantity * item.productPrice,
        0
    );

    const DELIVERY_FEE = 100;
    const DELIVERY_TIME = '2-3 business days';
    const CONTACT_NUMBER = '0113899836';
    const ORDER_STORAGE_KEY = 'thinktwice_orders';
    const totalAmount = subtotal + DELIVERY_FEE;

    const [formData, setFormData] = useState({
        fullName: '',
        phone: '',
        email: '',
        address: '',
        county: '',
        city: '',
        notes: ''
    });

    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentOrder, setCurrentOrder] = useState(null);

    const KENYAN_COUNTIES = [
        'Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet',
        'Embu', 'Garissa', 'Homa Bay', 'Isiolo', 'Kajiado',
        'Kakamega', 'Kericho', 'Kiambu', 'Kilifi', 'Kirinyaga',
        'Kisii', 'Kisumu', 'Kitui', 'Kwale', 'Laikipia',
        'Lamu', 'Machakos', 'Makueni', 'Mandera', 'Marsabit',
        'Meru', 'Migori', 'Mombasa', 'Murang\'a', 'Nairobi',
        'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua',
        'Nyeri', 'Samburu', 'Siaya', 'Taita-Taveta', 'Tana River',
        'Tharaka-Nithi', 'Trans Nzoia', 'Turkana', 'Uasin Gishu', 'Vihiga',
        'Wajir', 'West Pokot'
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    const saveOrderToLocalStorage = (createdOrder) => {
        try {
            const orderId = createdOrder?.id || `local-${Date.now()}`;
            const orderNumber = createdOrder?.id
                ? createdOrder.id.substring(0, 8).toUpperCase()
                : orderId.replace('local-', '').substring(0, 8).toUpperCase();

            const localOrder = {
                id: orderId,
                orderNumber,
                createdAt: createdOrder?.created_at || new Date().toISOString(),
                status: createdOrder?.status || 'pending',
                totalAmount: totalAmount,
                customerName: formData.fullName,
                customerPhone: formData.phone,
                customerEmail: formData.email || null,
                orderItems: cartItems.map(item => ({
                    productId: item.id,
                    productName: item.productName,
                    productImg: item.productImg,
                    productQuantity: item.productQuantity,
                    productPrice: item.productPrice,
                    variantName: item.variantName || null
                })),
                deliveryAddress: {
                    county: formData.county,
                    city: formData.city,
                    address: formData.address,
                    notes: formData.notes || null
                },
                paymentReference: null,
                paymentDate: null
            };

            const existingOrders = JSON.parse(localStorage.getItem(ORDER_STORAGE_KEY) || '[]');
            const filteredOrders = existingOrders.filter(order => order.id !== localOrder.id);
            localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify([localOrder, ...filteredOrders]));
            return localOrder;
        } catch (error) {
            console.error('Error saving order to localStorage:', error);
        }
        return null;
    };



    const handleSTKPush = async (e) => {
        if (e?.preventDefault) {
            e.preventDefault();
        }
        
        if (cartItems.length === 0) {
            setError('Your cart is empty');
            return;
        }

        if (!formData.phone || formData.phone.trim() === '') {
            setError('Please enter a phone number');
            return;
        }
        try {
            if (!paynectaService.validatePhoneNumber(formData.phone)) {
                setError('Invalid phone number. Use 07XX, 01XX, or 254XXX');
                return;
            }

            if (!paynectaService.validateAmount(totalAmount)) {
                setError('Invalid amount. Must be between 1 and 250,000 KES');
                return;
            }

            setIsSubmitting(true);
            setError('');

            let orderSnapshot = currentOrder;
            let orderId = currentOrder?.id;
            if (
                orderSnapshot &&
                (orderSnapshot.totalAmount !== totalAmount || orderSnapshot.customerPhone !== formData.phone)
            ) {
                orderSnapshot = null;
                orderId = null;
                setCurrentOrder(null);
            }

            if (!orderId) {
                const orderData = {
                    customer_name: formData.fullName,
                    customer_phone: formData.phone,
                    customer_email: formData.email || null,
                    total_amount: totalAmount,
                    status: 'pending',
                    items: {
                        products: cartItems.map(item => ({
                            product_id: item.id,
                            product_name: item.productName,
                            product_image: item.productImg,
                            quantity: item.productQuantity,
                            price: item.productPrice,
                            mode: item.mode || 'retail',
                            variant_id: item.variantId || null,
                            variant_name: item.variantName || null
                        })),
                        delivery_address: formData.address,
                        delivery_county: formData.county,
                        delivery_city: formData.city,
                        delivery_notes: formData.notes || null,
                        subtotal: subtotal,
                        delivery_fee: DELIVERY_FEE
                    }
                };

                const { data: createdOrders, error: dbError } = await supabase
                    .from('orders')
                    .insert([orderData])
                    .select();

                if (dbError) {
                    console.error('Error saving order:', dbError);
                    setError('Failed to create order. Please try again.');
                    setIsSubmitting(false);
                    return;
                }

                const createdOrder = Array.isArray(createdOrders) ? createdOrders[0] : null;
                orderSnapshot = saveOrderToLocalStorage(createdOrder);
                setCurrentOrder(orderSnapshot);
                orderId = createdOrder?.id;
                if (!orderId) {
                    setError('Order creation failed. Please try again.');
                    setIsSubmitting(false);
                    return;
                }
            }

            // Generate payment link with pre-filled data
            const paymentLink = paynectaService.generatePaymentLink(
                totalAmount,
                formData.phone,
                orderId
            );

            // Open payment in a fixed window - height adjusted to prevent scrolling
            const width = 500;
            const height = 750;
            const left = (window.screen.width / 2) - (width / 2);
            const top = (window.screen.height / 2) - (height / 2);
            
            window.open(
                paymentLink,
                'PaynectaPayment',
                `width=${width},height=${height},left=${left},top=${top},scrollbars=no,resizable=no,toolbar=no,menubar=no,location=no,status=no`
            );
            
            setIsSubmitting(false);

        } catch (err) {
            console.error('Error creating order:', err);
            setError('An error occurred. Please try again.');
            setIsSubmitting(false);
        }
    };

    if (cartItems.length === 0) {
        return (
            <section id="checkout">
                <div className="container my-5">
                    <div className="empty-cart text-center">
                        <h2>Your cart is empty</h2>
                        <a href="/home" className="btn btn-primary mt-3">Continue Shopping</a>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <Helmet>
                <title>ThinkTwiceOnline Store | Checkout</title>
            </Helmet>

            <section id="checkout">
                <div className="container my-5">
                    <Path pathName="Checkout" />

                    <div className="checkout-container">
                        <div className="checkout-form">
                            <h2>Checkout</h2>
                            {error && <div className="error-alert">{error}</div>}

                            <form onSubmit={handleSTKPush}>
                                <div className="payment-info-box stk-info">
                                    <h3>📱 M-Pesa STK Push</h3>
                                    <div className="payment-steps">
                                        <p><strong>How it works:</strong></p>
                                        <p>1. Fill in your details and click "Pay with M-Pesa"</p>
                                        <p>2. A payment prompt will appear on your phone</p>
                                        <p>3. Enter your M-Pesa PIN to confirm</p>
                                    </div>
                                    <div className="payment-note">
                                        <p>💡 We'll send the prompt to the number you provide</p>
                                        <p>📞 Need help? Call or WhatsApp: <strong>{CONTACT_NUMBER}</strong></p>
                                    </div>
                                </div>

                                <div className="delivery-info-box">
                                    <h3>🚚 Delivery Information</h3>
                                    <p>Delivery Time: <strong>{DELIVERY_TIME}</strong></p>
                                    {formData.county && (
                                        <>
                                            <p>Delivery to: <strong>{formData.county} County</strong></p>
                                            <p>Delivery Fee: <strong>KSh {DELIVERY_FEE}</strong></p>
                                        </>
                                    )}
                                    {!formData.county && (
                                        <p className="text-muted"><small>Select your county to see delivery fee</small></p>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Full Name *</label>
                                    <input
                                        type="text"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter your full name"
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Phone Number *</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="0712345678"
                                        />
                                        <small>We'll call you to confirm your order</small>
                                    </div>

                                    <div className="form-group">
                                        <label>Email (Optional)</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>County *</label>
                                    <select
                                        name="county"
                                        value={formData.county}
                                        onChange={handleInputChange}
                                        required
                                        className="form-select"
                                    >
                                        <option value="">Select your county</option>
                                        {KENYAN_COUNTIES.map((county) => (
                                            <option key={county} value={county}>
                                                {county}
                                            </option>
                                        ))}
                                    </select>
                                    <small>Select the county for delivery</small>
                                </div>

                                <div className="form-group">
                                    <label>City/Town *</label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter your city or town"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Delivery Address *</label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter your delivery address (e.g., Building name, Street, Area)"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Order Notes (Optional)</label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        rows="4"
                                        placeholder="Please specify:&#10;• Size (e.g., Small, Medium, Large, XL)&#10;• Color preferences&#10;• Delivery instructions (e.g., Call before delivery, Leave at gate)&#10;• Any other special requests"
                                    ></textarea>
                                    <small className="form-help-text">
                                        <i className="bi bi-info-circle"></i> Include size, color, or any delivery instructions to help us serve you better
                                    </small>
                                </div>

                                <button type="submit" className="btn-place-order" disabled={isSubmitting}>
                                    {isSubmitting ? '⏳ Sending M-Pesa prompt...' : `📱 Pay KSh ${totalAmount.toLocaleString()} with M-Pesa`}
                                </button>
                            </form>
                        </div>

                        <div className="order-summary">
                            <h2>Order Summary</h2>

                            <div className="summary-items">
                                {cartItems.map((item, index) => (
                                    <div key={index} className="summary-item">
                                        <img src={item.productImg} alt={item.productName} />
                                        <div className="item-details">
                                            <h4>{item.productName}</h4>
                                            <p>Qty: {item.productQuantity}</p>
                                        </div>
                                        <span className="item-price">KSh {(item.productPrice * item.productQuantity).toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="summary-totals">
                                <div className="summary-row">
                                    <span>Subtotal</span>
                                    <span>KSh {subtotal.toLocaleString()}</span>
                                </div>
                                <div className="summary-row delivery-info">
                                    <div>
                                        <span>Delivery Fee</span>
                                        <small className="d-block text-muted">{DELIVERY_TIME}</small>
                                    </div>
                                    <span>KSh {DELIVERY_FEE.toLocaleString()}</span>
                                </div>
                                <div className="summary-row total">
                                    <span>Total</span>
                                    <span>KSh {(subtotal + DELIVERY_FEE).toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Checkout;
